import { useApp } from "@/hooks/use-app-context";
import { cn } from "@/lib/utils";
import { useThemeConfig, type TabMode } from "./use-theme-config";
import { ManualThemeForm } from "./ManualThemeForm";
import { ExcelUploadForm } from "./ExcelUploadForm";
import { ExcelPreviewDialog } from "./ExcelPreviewDialog";

/**
 * Adding a theme, by hand or from an Excel workbook of questions. Lives in the
 * "Add a theme" tab of "Themes and phrases", the only place themes are
 * created; the imported themes are listed in the tab next to it.
 */
export function ThemeCreatePanel() {
  const { t } = useApp();
  const themeT = t.admin.themeConfig;

  const {
    activeTab, setActiveTab,
    title, setTitle, description, setDescription, examplesText, setExamplesText,
    isSaving,
    excelTitle, setExcelTitle, excelDescription, setExcelDescription,
    excelSector, setExcelSector, excelSectorCustom, setExcelSectorCustom, excelLanguage, setExcelLanguage,
    excelFile, isExcelLoading, previewData, isPreviewOpen, setIsPreviewOpen, fileInputRef,
    handleFileSelect, handleRemoveFile, handleSave, handleClear, handleExcelSubmit,
    languages, isLanguagesLoading,
    sectors, isSelectorsLoading,
  } = useThemeConfig();

  return (
    <div className="flex w-full flex-col gap-6 pt-7">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-2xl text-sm leading-6 text-muted-foreground">{themeT.description}</p>
        <SegmentedTabs value={activeTab} onChange={setActiveTab} labels={{ manual: themeT.tabs.manual, excel: themeT.tabs.excel }} />
      </div>

      {activeTab === "manual" ? (
        <ManualThemeForm
          title={title}
          description={description}
          examplesText={examplesText}
          isSaving={isSaving}
          t={themeT}
          onTitleChange={setTitle}
          onDescriptionChange={setDescription}
          onExamplesChange={setExamplesText}
          onSave={handleSave}
          onClear={handleClear}
        />
      ) : (
        <ExcelUploadForm
          excelTitle={excelTitle}
          excelDescription={excelDescription}
          excelSector={excelSector}
          excelSectorCustom={excelSectorCustom}
          excelLanguage={excelLanguage}
          excelFile={excelFile}
          isExcelLoading={isExcelLoading}
          previewData={previewData}
          fileInputRef={fileInputRef}
          t={themeT}
          onTitleChange={setExcelTitle}
          onDescriptionChange={setExcelDescription}
          onSectorChange={setExcelSector}
          onSectorCustomChange={setExcelSectorCustom}
          onLanguageChange={setExcelLanguage}
          onFileSelect={handleFileSelect}
          onRemoveFile={handleRemoveFile}
          onOpenPreview={() => setIsPreviewOpen(true)}
          onSubmit={handleExcelSubmit}
          languages={languages}
          isLanguagesLoading={isLanguagesLoading}
          sectors={sectors}
          isSelectorsLoading={isSelectorsLoading}
        />
      )}

      <ExcelPreviewDialog
        isOpen={isPreviewOpen}
        previewData={previewData}
        isExcelLoading={isExcelLoading}
        t={themeT}
        onClose={() => setIsPreviewOpen(false)}
        onSubmit={handleExcelSubmit}
      />
    </div>
  );
}

function SegmentedTabs({ value, onChange, labels }: { value: TabMode; onChange: (v: TabMode) => void; labels: Record<TabMode, string> }) {
  return (
    <div role="group" className="grid min-h-10 shrink-0 grid-cols-2 gap-1 rounded-lg border border-border/60 bg-surface-2 p-1 sm:w-[300px]">
      {(["manual", "excel"] as const).map((tab) => (
        <button
          key={tab}
          type="button"
          aria-pressed={value === tab}
          onClick={() => onChange(tab)}
          className={cn(
            "rounded-md px-3 py-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            value === tab ? "border border-border/60 bg-background text-foreground" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {labels[tab]}
        </button>
      ))}
    </div>
  );
}
